<template>
    <div>
        <!-- The table (paging, sort, search, reloads, both layouts) is DuckDbDataTable's; the item
             row, the sub-item rows, the mobile card and the report button are this grid's slots -->
        <DuckDbDataTable :title="t('itemsGrid')"
                         :headers="headers"
                         :fetch-page="fetchPage"
                         :filter-context="filterContext"
                         :data-loaded="dataLoaded"
                         :default-sort="defaultSort"
                         :search-debounce="300"
                         item-value="id"
                         must-sort
                         @items-loaded="onItemsLoaded">
            <template #toolbar-actions>
                <v-btn v-if="generateReport"
                       icon
                       variant="plain"
                       @click="showSummaryReportDialog = true">
                    <v-icon>mdi-download</v-icon>
                </v-btn>
            </template>

            <template #mobile-toolbar-actions>
                <v-btn v-if="generateReport"
                       icon
                       variant="plain"
                       @click="showSummaryReportDialog = true">
                    <v-icon>mdi-download</v-icon>
                </v-btn>
            </template>

            <template #desktop-row="{ item, columns }">
                <tr>
                    <td class="client-id-cell">
                        <div class="d-flex align-center">
                            <template v-if="hasSubItems(item, 1)">
                                <v-btn :icon="expandIcon(item)"
                                       @click="toggleExpand(item)"
                                       variant="plain"
                                       class="mr-1"
                                       style="width: 24px; height:24px;"></v-btn>
                            </template>

                            <v-chip label variant="text">
                                <v-icon :icon="item.categoryIcon" class="mr-2"/>

                                <a @click.prevent="openUrl(`https://${apiHost}/${projectCode}/${item.type === 'IssueTemplate' ? 'issues?issue' : 'processes?process'}-client-ids=${item.clientId}&subcategory-ids=${item.subcategoryId}`)"
                                   href="#"
                                   style="color: inherit;">
                                    {{ item.clientId }}
                                </a>
                            </v-chip>
                        </div>
                    </td>


                    <td>
                        <div class="d-flex flex-column align-start">
                            <span class="text-start">{{ item.profileTitle }}</span>
                            <span v-if="!hasSubItems(item, 1)" style="color: #888;" class="text-start text-caption">
                                {{ item.formTemplateTitle !== item.profileTitle ? item.formTemplateTitle : '' }}
                            </span>
                        </div>
                    </td>

                    <td>
                        <template v-if="!hasSubItems(item, 1)">
                            {{ item.subItems[0].assigneeTeamName }}
                        </template>
                    </td>

                    <td>
                        <div class="d-flex align-center">
                            <template v-if="hasAssignees(item)">
                                <!-- Display the first 5 avatars -->
                                <v-tooltip v-for="user in getUniqueUsers(item.subItems, 5)"
                                           :key="user.id"
                                           :text="user.displayName"
                                           location="bottom">
                                    <template v-slot:activator="{ props }">
                                        <v-avatar v-bind="props" size="28" class="mr-1">
                                            <v-img v-if="user.avatarUrl"
                                                   :src="getAvatarUrl(user.avatarUrl)"
                                                   alt="User Avatar"></v-img>
                                            <span v-else
                                                class="d-flex justify-center align-center text-caption white--text"
                                                :style="{
                                                backgroundColor: getRandomColor(user.id),
                                                width: '100%',
                                                height: '100%',
                                                borderRadius: '50%'
                                              }">
                                                    {{ getInitials(user.displayName || '') }}
                                                </span>
                                            </v-avatar>
                                        </template>
                                    </v-tooltip>

                                    <!-- If there are more than 5, show a +[number] with a vertical list in tooltip -->
                                    <v-tooltip v-if="getUniqueUsers(item.subItems, 999).length > 5" location="bottom">
                                        <template v-slot:activator="{ props }">
                                            <span v-bind="props" class="mr-1">
                                                +{{ getUniqueUsers(item.subItems, 999).length - 5 }}
                                            </span>
                                        </template>
                                        <template v-slot:default>

                                            <v-list-item v-for="user in getUniqueUsers(item.subItems, 999).slice(5)"
                                                         :key="user.id"
                                                         class="px-2 py-1">
                                                <template v-slot:prepend>
                                                    <v-avatar size="22" class="mr-2">
                                                        <v-img v-if="user.avatarUrl"
                                                               :src="getAvatarUrl(user.avatarUrl)"
                                                               alt="User Avatar"></v-img>
                                                        <span v-else
                                                              class="d-flex justify-center align-center text-caption"
                                                              :style="{
                                                                    backgroundColor: getRandomColor(user.id),
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    borderRadius: '50%'
                                                                }">
                                                            {{ getInitials(user.displayName || '') }}
                                                        </span>
                                                    </v-avatar>
                                                </template>
                                                <v-list-item-title class="text-caption">
                                                    {{ user.displayName || 'Unknown User' }}
                                                </v-list-item-title>
                                            </v-list-item>

                                        </template>
                                    </v-tooltip>
                                </template>
                        </div>
                    </td>

                    <td>
                        <div>
                            <v-icon icon="mdi-map" size="small" class="mr-1"></v-icon>
                            <a @click.prevent="openUrl(`https://${apiHost}/${projectCode}/plan-viewer/${item.drawingId}`)"
                               href="#"
                               style="color: inherit;">
                                {{ item.drawingTitle }}
                            </a>
                        </div>
                        <div>
                            <v-icon icon="mdi-map-marker" size="small" class="mr-1"></v-icon>
                            <a @click.prevent="openUrl(`https://${apiHost}/${projectCode}/plan-viewer/${item.drawingId}#view/location${item.locationId}`)"
                               href="#"
                               style="color: inherit;">
                                {{ item.locationTitle }}
                            </a>
                        </div>
                    </td>

                    <td>
                        <div class="d-flex flex-column">
                            <span v-if="item.createdByTeamName">
                                <v-icon icon="mdi-account-group" size="small" class="mr-1"></v-icon>{{ item.createdByTeamName }}
                            </span>
                            <span v-if="item.createdByUserName" class="text-caption">
                                <v-icon icon="mdi-account" size="small" class="mr-1"></v-icon>{{ item.createdByUserName }}
                            </span>
                        </div>
                    </td>
                    <td>
                        <div class="d-flex flex-column">
                            <span>{{ formatDate(item.updatedDate) }}</span>
                            <span style="color: #888;" class="text-caption">{{ getDaysSinceText(item.updatedDate) }}</span>
                        </div>
                    </td>
                    <td>
                        <v-chip :style="{
                            backgroundColor: item.latestStatusColor,
                            color: 'white'
                        }"
                                label>
                            {{ item.latestStatusTitle }}
                        </v-chip>
                    </td>

                </tr>

                <tr v-if="isExpanded(item) && item.subItems.length > 1">
                    <td :colspan="columns.length">
                        <v-data-table :headers="subItemHeaders" :items="item.subItems" :items-per-page="-1" item-value="id" hide-default-footer class="elevation-0">
                            <template #[`item.itemTemplateId`]="{ item: subItem }">
                                {{ subItem.formTemplateTitle }}
                            </template>

                            <template #[`item.assigneeTeamId`]="{ item: subItem }">
                                {{ subItem.assigneeTeamName || '-' }}
                            </template>

                            <template #[`item.assigneeUserIds`]="{ item: subItem }">
                                <div class="d-flex align-center">
                                    <template v-if="subItem.assigneeUsers && subItem.assigneeUsers.length > 0">
                                        <v-tooltip v-for="user in subItem.assigneeUsers.slice(0, 5)"
                                                   :key="user.id"
                                                   :text="user.displayName"
                                                   location="bottom">
                                            <template v-slot:activator="{ props }">
                                                <v-avatar v-bind="props" size="28" class="mr-1">
                                                    <v-img v-if="user.avatarUrl"
                                                           :src="getAvatarUrl(user.avatarUrl)"
                                                           alt="User Avatar"></v-img>
                                                    <span v-else
                                                          class="d-flex justify-center align-center text-caption white--text"
                                                          :style="{
                                                                backgroundColor: getRandomColor(user.id),
                                                                width: '100%',
                                                                height: '100%',
                                                                borderRadius: '50%'
                                                            }">
                                                            {{ getInitials(user.displayName || '') }}
                                                        </span>
                                                    </v-avatar>
                                                </template>
                                            </v-tooltip>

                                            <span v-if="subItem.assigneeUserIds && subItem.assigneeUserIds.length > 5" class="ml-1">
                                                +{{ subItem.assigneeUserIds.length - 5 }}
                                            </span>
                                        </template>

                                        <span v-else>-</span>
                                    </div>
                                </template>

                                <template #[`item.updatedDate`]="{ item: subItem }">
                                    <div class="d-flex flex-column">
                                        <span>{{ formatDate(subItem.statusCreatedDate) }}</span>
                                        <span style="color: #888;" class="text-caption">{{ getDaysSinceText(subItem.statusCreatedDate) }}</span>
                                    </div>
                                </template>

                                <template #[`item.statusId`]="{ item: subItem }">
                                    <v-chip v-if="subItem.statusTitle"
                                            :style="{
                                                backgroundColor: subItem.statusColor || '#999',
                                                color: 'white'
                                            }"
                                            label>
                                        {{ subItem.statusTitle }}
                                    </v-chip>
                                    <span v-else>-</span>
                                </template>
                            </v-data-table>
                        </td>
                    </tr>
            </template>

            <template #mobile-item="{ item }">
                <v-card class="mb-2" variant="flat">
                    <v-card-title class="d-flex justify-space-between align-center">
                        <v-chip label variant="text">
                            <v-icon v-if="itemTemplates && categories && item.profileId && itemTemplates[item.profileId] && categories[itemTemplates[item.profileId].categoryId]" :icon="getIconClass(categories[itemTemplates[item.profileId].categoryId].icon)" class="mr-2"></v-icon>
                            <a v-if="itemTemplates && item.profileId && itemTemplates[item.profileId]"
                               @click.prevent="openUrl(`https://${apiHost}/${projectCode}/${item.type === 'IssueTemplate' ? 'issues?issue' : 'processes?process'}-client-ids=${item.clientId}&subcategory-ids=${itemTemplates[item.profileId].subcategoryId}`)"
                               href="#"
                               style="color: inherit;">
                                {{ item.clientId }}
                            </a>
                            <span v-else>{{ item.clientId }}</span>
                        </v-chip>
                        <v-chip v-if="statusTemplates && item.subItems && item.subItems[0] && item.subItems[0].statusId && statusTemplates[item.subItems[0].statusId]"
                                :style="{
                                backgroundColor: statusTemplates[item.subItems[0].statusId].color,
                                color: 'white',
                                marginTop:'5px'
                                }"
                                label>
                            {{ statusTemplates[item.subItems[0].statusId].title }}
                        </v-chip>
                    </v-card-title>

                    <v-card-text>
                        <div class="mb-2 text-h6">
                            {{ item.profileTitle }}
                        </div>

                        <div class="mb-2 d-flex justify-space-between" v-if="item.subItems.length === 1">
                            <strong style="flex-shrink: 0">{{ t('assignedTo') }}:</strong>
                            <div class="d-flex flex-column align-end">
                                <span>{{ item.subItems[0].assigneeTeamName ? item.subItems[0].assigneeTeamName : '-' }}</span>
                                <div class="d-flex justify-end mt-1">
                                    <template v-if="item.subItems[0].assigneeUserIds && item.subItems[0].assigneeUserIds.length > 0">
                                        <v-avatar v-for="userId in item.subItems[0].assigneeUserIds.slice(0, 3)"
                                                  :key="userId"
                                                  size="28"
                                                  class="mr-1">
                                            <v-img v-if="users[userId] && users[userId].avatarUrl"
                                                   :src="getAvatarUrl(users[userId].avatarUrl)"
                                                   alt="User Avatar"></v-img>
                                            <span v-else
                                                  class="d-flex justify-center align-center text-caption white--text"
                                                  :style="{
                                            backgroundColor: getRandomColor(userId),
                                            width: '100%',
                                            height: '100%',
                                            borderRadius: '50%'
                                        }">
                                                {{ getInitials(users[userId] ? users[userId].displayName : '') }}
                                            </span>
                                        </v-avatar>
                                        <span v-if="item.subItems[0].assigneeUserIds.length > 3" class="ml-1">
                                            +{{ item.subItems[0].assigneeUserIds.length - 3 }}
                                        </span>
                                    </template>
                                </div>
                            </div>
                        </div>

                        <div class="mb-2 d-flex justify-space-between">
                            <strong style="flex-shrink: 0">{{ t('location') }}:</strong>
                            <div class="d-flex flex-column align-end" style="max-width: calc(100% - 80px);">
                                <div style="width: 100%; display: flex; justify-content: flex-end;">
                                    <div style="display: flex; align-items: flex-start; text-align: right;">
                                        <v-icon icon="mdi-map" size="small" style="flex-shrink: 0; margin-right: 4px; margin-top: 2px;"></v-icon>
                                        <a @click.prevent="openUrl(`https://${apiHost}/${projectCode}/plan-viewer/${item.drawingId}`)"
                                            href="#"
                                            style="color: inherit;">
                                            {{ item.drawingTitle }}
                                        </a>
                                    </div>
                                </div>
                                <div style="width: 100%; display: flex; justify-content: flex-end; margin-top: 4px;">
                                    <div style="display: flex; align-items: flex-start; text-align: right;">
                                        <v-icon icon="mdi-map-marker" size="small" style="flex-shrink: 0; margin-right: 4px; margin-top: 2px;"></v-icon>
                                        <a @click.prevent="openUrl(`https://${apiHost}/${projectCode}/plan-viewer/${item.drawingId}#view/location${item.locationId}`)"
                                            href="#"
                                            style="color: inherit;">
                                            {{ item.locationTitle }}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="mb-2 d-flex justify-space-between">
                            <strong style="flex-shrink: 0">{{ t('createdBy') }}:</strong>
                            <div class="d-flex flex-column align-end">
                                <div class="d-flex align-center" v-if="item.createdByTeamName">
                                    <v-icon icon="mdi-account-group" size="small" class="mr-1"></v-icon>{{ item.createdByTeamName }}
                                </div>
                                <div class="d-flex align-center text-caption" v-if="item.createdByUserName">
                                    <v-icon icon="mdi-account" size="small" class="mr-1"></v-icon>{{ item.createdByUserName }}
                                </div>
                            </div>
                        </div>

                        <div class="mb-2" style="display: grid; grid-template-columns: auto 1fr auto; gap: 8px; align-items: center;">
                            <strong style="flex-shrink: 0">{{ t('updatedDate') }}:</strong>
                            <span class="d-flex align-center" :style="{
                                'text-align': item.subItems.length > 1 ? 'center' : 'right',
                                'justify-content': item.subItems.length > 1 ? 'center' : 'flex-end'
                            }">
                                {{ formatDate(item.updatedDate) }}
                            </span>
                            <template v-if="item.subItems.length > 1">
                                <v-btn prepend-icon="mdi-file-document-outline"
                                       :text="item.subItems.length"
                                       variant="outlined"
                                       @click="showSubItemsDialog = true; selectedSubItems = item"></v-btn>
                            </template>
                            <template v-else>
                                <span></span> <!-- Empty column when button is not shown -->
                            </template>
                        </div>

                    </v-card-text>
                </v-card>
            </template>
        </DuckDbDataTable>

        <ItemsGridPaginatedReportsDialog
            v-if="generateReport"
            v-model="showSummaryReportDialog"
            :generating="reportBusy"
            @generate="handleGenerateReport"
        />
    </div>
</template>

<script src="./model.js"></script>
<style src="./style.css"></style>
